import {InputManager} from "./input.manager";
import {CurrencyMaskConfig} from "./currency-mask.config";


export class InputService {
    PER_AR_NUMBER: Map<string, string> = new Map<string, string>();

    initialize() {
        this.PER_AR_NUMBER.set("\u06F0", "0");
        this.PER_AR_NUMBER.set("\u06F1", "1");
        this.PER_AR_NUMBER.set("\u06F2", "2");
        this.PER_AR_NUMBER.set("\u06F3", "3");
        this.PER_AR_NUMBER.set("\u06F4", "4");
        this.PER_AR_NUMBER.set("\u06F5", "5");
        this.PER_AR_NUMBER.set("\u06F6", "6");
        this.PER_AR_NUMBER.set("\u06F7", "7");
        this.PER_AR_NUMBER.set("\u06F8", "8");
        this.PER_AR_NUMBER.set("\u06F9", "9");

        this.PER_AR_NUMBER.set("\u0660", "0");
        this.PER_AR_NUMBER.set("\u0661", "1");
        this.PER_AR_NUMBER.set("\u0662", "2");
        this.PER_AR_NUMBER.set("\u0663", "3");
        this.PER_AR_NUMBER.set("\u0664", "4");
        this.PER_AR_NUMBER.set("\u0665", "5");
        this.PER_AR_NUMBER.set("\u0666", "6");
        this.PER_AR_NUMBER.set("\u0667", "7");
        this.PER_AR_NUMBER.set("\u0668", "8");
        this.PER_AR_NUMBER.set("\u0669", "9");
    }

    inputManager: InputManager;

    constructor(public htmlInputElement: any, private options: CurrencyMaskConfig) {
        this.inputManager = new InputManager(htmlInputElement);
        this.initialize()
    }

    addNumber(keyCode: number): void {
        let {decimal, precision} = this.options;
        let keyChar = String.fromCharCode(keyCode);

        // Cho phép gõ '.' như dấu thập phân khi decimal là ','
        if (keyChar === "." && decimal === ",") {
            keyChar = decimal;
        }

        const isDigit = /[0-9\u0660-\u0669\u06F0-\u06F9]/.test(keyChar);
        const isDecimalSep = keyChar === decimal;

        if (!isDigit && !isDecimalSep) {
            return;
        }

        if (isDecimalSep) {
            if (!(precision > 0)) {
                return;
            }
            if ((this.rawValue || "").indexOf(decimal) >= 0) {
                return;
            }
            if (!this.rawValue) {
                this.rawValue = "0" + decimal;
                this.updateFieldValue((this.rawValue || "").length);
                return;
            }
        }

        if (isDigit && precision > 0 && this.rawValue) {
            const decIdx = this.rawValue.indexOf(decimal);
            if (decIdx >= 0) {
                const selectionStart = this.inputSelection.selectionStart;
                const selectionEnd = this.inputSelection.selectionEnd;
                if (selectionStart === selectionEnd && selectionStart > decIdx) {
                    const decPart = this.rawValue.substring(decIdx + 1).replace(/[^\d\u0660-\u0669\u06F0-\u06F9]/g, "");
                    if (decPart.length >= precision) {
                        return;
                    }
                }
            }
        }

        if (!this.rawValue) {
            this.rawValue = "";
        }

        let selectionStart = this.inputSelection.selectionStart;
        let selectionEnd = this.inputSelection.selectionEnd;
        this.rawValue = this.rawValue.substring(0, selectionStart) + keyChar + this.rawValue.substring(selectionEnd, this.rawValue.length);
        this.updateFieldValue(selectionStart + 1);
    }

    applyMask(isNumber: boolean, rawValue: string): string {
        let {allowNegative, decimal, precision, prefix, suffix, thousands} = this.options;

        if (rawValue === "null" || rawValue === "undefined") {
            return null;
        }

        // precision = 0: format số nguyên có dấu phân cách nghìn
        if (!(precision > 0)) {
            rawValue = isNumber ? new Number(rawValue).toFixed(0) : rawValue;
            let onlyNumbers = rawValue.replace(/[^0-9\u0660-\u0669\u06F0-\u06F9]/g, "");

            if (!onlyNumbers) {
                return "";
            }

            let integerPart = onlyNumbers
                .replace(/^\u0660*/g, "")
                .replace(/^\u06F0*/g, "")
                .replace(/^0*/g, "")
                .replace(/\B(?=([0-9\u0660-\u0669\u06F0-\u06F9]{3})+(?![0-9\u0660-\u0669\u06F0-\u06F9]))/g, thousands);

            if (thousands && integerPart.startsWith(thousands)) {
                integerPart = integerPart.substring(1);
            }

            if (integerPart == "") {
                integerPart = "0";
            }

            let isZero = parseInt(integerPart) == 0;
            let operator = (rawValue.indexOf("-") > -1 && allowNegative && !isZero) ? "-" : "";
            return operator + prefix + integerPart + suffix;
        }

        // precision > 0: số nguyên có dấu nghìn; thập phân chỉ hiện khi người dùng gõ dấu thập phân
        return this.applyMaskFlexible(isNumber, rawValue);
    }

    private applyMaskFlexible(isNumber: boolean, rawValue: string): string {
        let {allowNegative, decimal, precision, prefix, suffix, thousands} = this.options;
        let operator = "";
        let intDigits = "";
        let decDigits = "";
        let hasDecimalSeparator = false;

        if (isNumber) {
            let num = Number(rawValue);
            if (!isFinite(num)) {
                return "";
            }
            if (num < 0) {
                if (allowNegative) {
                    operator = "-";
                }
                num = Math.abs(num);
            }
            if (num === 0) {
                return prefix + "0" + suffix;
            }

            let fixed = num.toFixed(precision);
            let parts = fixed.split(".");
            intDigits = (parts[0] || "0").replace(/^0+(?=\d)/, "");
            decDigits = (parts[1] || "").replace(/0+$/, "");
            hasDecimalSeparator = decDigits.length > 0;
        } else {
            let work = String(rawValue == null ? "" : rawValue);
            if (prefix && work.indexOf(prefix) === 0) {
                work = work.substring(prefix.length);
            }
            if (suffix && work.length >= suffix.length && work.substring(work.length - suffix.length) === suffix) {
                work = work.substring(0, work.length - suffix.length);
            }

            if (allowNegative && work.indexOf("-") > -1) {
                operator = "-";
            }

            // Chỉ giữ số, dấu thập phân và dấu nghìn
            let normalized = work.replace(/[^\d,.\u0660-\u0669\u06F0-\u06F9]/g, "");

            if (normalized.indexOf(decimal) >= 0) {
                if (thousands) {
                    normalized = normalized.split(thousands).join("");
                }
                // Bỏ mọi '.' còn lại (không phải dấu thập phân VN)
                normalized = normalized.replace(/\./g, "");
                hasDecimalSeparator = true;
                let idx = normalized.indexOf(decimal);
                intDigits = normalized.substring(0, idx).replace(/[^\d\u0660-\u0669\u06F0-\u06F9]/g, "");
                decDigits = normalized.substring(idx + 1)
                    .replace(new RegExp("\\" + decimal, "g"), "")
                    .replace(/[^\d\u0660-\u0669\u06F0-\u06F9]/g, "");
            } else {
                // Chưa gõ dấu thập phân: mọi '.' đều là dấu nghìn
                if (thousands) {
                    normalized = normalized.split(thousands).join("");
                }
                normalized = normalized.replace(/\./g, "");
                intDigits = normalized.replace(/[^\d\u0660-\u0669\u06F0-\u06F9]/g, "");
                decDigits = "";
                hasDecimalSeparator = false;
            }

            if (!intDigits && !hasDecimalSeparator && !decDigits) {
                return "";
            }

            intDigits = intDigits.replace(/^0+(?=\d)/, "");
            if (intDigits === "") {
                intDigits = "0";
            }
        }

        if (decDigits.length > precision) {
            decDigits = decDigits.substring(0, precision);
        }

        let formattedInt = intDigits.replace(
            /\B(?=([0-9\u0660-\u0669\u06F0-\u06F9]{3})+(?![0-9\u0660-\u0669\u06F0-\u06F9]))/g,
            thousands || ""
        );

        if (thousands && formattedInt.startsWith(thousands)) {
            formattedInt = formattedInt.substring(1);
        }

        let result = formattedInt;
        if (hasDecimalSeparator) {
            result += decimal + decDigits;
        }

        return operator + prefix + result + suffix;
    }

    clearMask(rawValue: string): number {
        if (this.isNullable() && rawValue === "")
            return null;

        let value = (rawValue || "0").replace(this.options.prefix, "").replace(this.options.suffix, "");

        if (this.options.thousands) {
            value = value.replace(new RegExp("\\" + this.options.thousands, "g"), "");
        }

        if (this.options.decimal) {
            value = value.replace(this.options.decimal, ".");
        }

        this.PER_AR_NUMBER.forEach((val: string, key: string) => {
            const re = new RegExp(key, "g");
            value = value.replace(re, val);
        });
        return parseFloat(value);
    }

    changeToNegative(): void {
        if (this.options.allowNegative && this.rawValue != "" && this.rawValue.charAt(0) != "-" && this.value != 0) {
            this.rawValue = "-" + this.rawValue;
        }
    }

    changeToPositive(): void {
        this.rawValue = this.rawValue.replace("-", "");
    }

    removeNumber(keyCode: number): void {
        if (this.isNullable() && this.value == 0) {
            this.rawValue = null;
            return;
        }

        let selectionEnd = this.inputSelection.selectionEnd;
        let selectionStart = this.inputSelection.selectionStart;

        if (selectionStart > this.rawValue.length - this.options.suffix.length) {
            selectionEnd = this.rawValue.length - this.options.suffix.length;
            selectionStart = this.rawValue.length - this.options.suffix.length;
        }

    let move = this.rawValue.substr(selectionStart - 1, 1).match(/\d/) ? 0 : -1;
    if ((
          keyCode == 8 &&
          selectionStart - 1 === 0 &&
          !(this.rawValue.substr(selectionStart, 1).match(/\d/))
        ) ||
        (
          (keyCode == 46 || keyCode == 63272) &&
          selectionStart === 0 &&
          !(this.rawValue.substr(selectionStart + 1, 1).match(/\d/))
        )
    ) {
      move = 1;
    };
    selectionEnd = keyCode == 46 || keyCode == 63272 ? selectionEnd + 1 : selectionEnd;
    selectionStart = keyCode == 8 ? selectionStart - 1 : selectionStart;
    this.rawValue = this.rawValue.substring(0, selectionStart) + this.rawValue.substring(selectionEnd, this.rawValue.length);
    this.updateFieldValue(selectionStart + move);
  }

    updateFieldValue(selectionStart?: number): void {
        let newRawValue = this.applyMask(false, this.rawValue || "");
        selectionStart = selectionStart == undefined ? this.rawValue.length : selectionStart;
        this.inputManager.updateValueAndCursor(newRawValue, this.rawValue.length, selectionStart);
    }

    updateOptions(options: any): void {
        let value: number = this.value;
        this.options = options;
        this.value = value;
    }

    prefixLength(): any {
        return this.options.prefix.length;
    }

    isNullable() {
        return this.options.nullable;
    }

    get canInputMoreNumbers(): boolean {
        return this.inputManager.canInputMoreNumbers;
    }

    get inputSelection(): any {
        return this.inputManager.inputSelection;
    }

    get rawValue(): string {
        return this.inputManager.rawValue;
    }

    set rawValue(value: string) {
        this.inputManager.rawValue = value;
    }

    get storedRawValue(): string {
        return this.inputManager.storedRawValue;
    }

    get value(): number {
        return this.clearMask(this.rawValue);
    }

    set value(value: number) {
        if (value === null || value === undefined) {
            this.rawValue = this.isNullable() ? null : this.applyMask(true, "0");
            return;
        }

        // Đang nhập: giữ nguyên chuỗi hiển thị (kể cả dấu ',' cuối) nếu cùng giá trị số
        if (this.htmlInputElement && this.htmlInputElement === document.activeElement) {
            const current = this.rawValue || "";
            if (current !== "" && this.clearMask(current) === Number(value)) {
                return;
            }
        }

        this.rawValue = this.applyMask(true, "" + value);
    }
}
